import { Component } from '@angular/core';
import { FormControl, FormGroup, NgModel, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { error } from 'jquery';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Union } from 'src/app/models/union';
import { UnionService } from 'src/app/services/union.service';
import { RoleService } from 'src/app/services/role.service';

@Component({
  selector: 'app-union-manage',
  templateUrl: './union-manage.component.html',
  styleUrls: ['./union-manage.component.scss']
})
export class UnionManageComponent {
  $subscriptions: Subscription[] = [];
  unionFrom!: FormGroup;
  editUnion: boolean = false;
  financialYearOptions: number[] = [];
  isLoading: boolean = false;
  showPreview: any = {
    assessment: false,
    tax: false
  };
  variableSearchTerm: string = '';
  recentlyCopied: string = '';
  showCopyToast: boolean = false;
  lastCopiedVariable: string = '';
  allVariables: any[] = [];
  filteredVariables: any[] = [];

  constructor(
    private unionService: UnionService,
    private toastr: ToastrService,
    private roleService: RoleService
  ) { 
    this.initializeVariables();
  }

  ngOnInit(): void {
    this.editUnion = false;
    this.generateFinancialYearOptions();
    this.initializeForm();
    this.getUnion();
  }

  ngOnDestroy(): void {
    this.$subscriptions.forEach(s=> s.unsubscribe());
  }

  canEditFinancialYear(): boolean {
    return this.roleService.hasRole('ROLE_MANAGER') || this.roleService.isSuperAdmin();
  }

  generateFinancialYearOptions(): void {
    const currentYear = new Date().getFullYear();
    this.financialYearOptions = [];
    
    // Generate years from current year - 5 to current year + 5
    for (let year = currentYear - 5; year <= currentYear + 5; year++) {
      this.financialYearOptions.push(year);
    }
  }

  // Form validation methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.unionFrom.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  isFieldValid(fieldName: string): boolean {
    const field = this.unionFrom.get(fieldName);
    return !!(field && field.valid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.unionFrom.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return `${fieldName} অবশ্যই পূরণ করতে হবে`;
      }
      if (field.errors['minlength']) {
        return `কমপক্ষে ${field.errors['minlength'].requiredLength} অক্ষর প্রয়োজন`;
      }
      if (field.errors['maxlength']) {
        return `সর্বোচ্চ ${field.errors['maxlength'].requiredLength} অক্ষর অনুমোদিত`;
      }
    }
    return '';
  }

  // Custom validator for house tax amounts
  private numericValidator: ValidatorFn = (control: AbstractControl) => {
    const value = control.value;
    if (value && isNaN(value)) {
      return { numeric: true };
    }
    return null;
  };

  // House Types Helper Methods
  getConfiguredHouseTypesCount(): number {
    const houseTypes = ['ek_chala', 'dui_chala', 'char_chala', 'adha_paka', 'paka', 
                       'ek_tola', 'dui_tola', 'tin_tola', 'char_tola', 'bohutol'];
    
    return houseTypes.filter(type => this.hasHouseTypeValue(type)).length;
  }

  hasHouseTypeValue(fieldName: string): boolean {
    const value = this.unionFrom.get(fieldName)?.value;
    const taxValue = this.unionFrom.get(fieldName + '_kor')?.value;
    return !!(value && parseFloat(value) > 0) || !!(taxValue && parseFloat(taxValue) > 0);
  }

  formatCurrency(event: any, fieldName: string): void {
    const input = event.target;
    let value = input.value.replace(/[^\d]/g, ''); // Remove all non-numeric characters
    
    if (value) {
      // Format the number with commas (e.g., 100,000)
      const formattedValue = new Intl.NumberFormat('en-US').format(parseInt(value));
      input.value = formattedValue;
      
      // Update the form control with the raw numeric value
      this.unionFrom.get(fieldName)?.setValue(parseInt(value));
    } else {
      this.unionFrom.get(fieldName)?.setValue('');
    }
  }

  getTaxPercentage(valueField: string, taxField: string): number {
    const value = this.unionFrom.get(valueField)?.value;
    const tax = this.unionFrom.get(taxField)?.value;
    
    if (value && tax && parseFloat(value) > 0 && parseFloat(tax) > 0) {
      const percentage = (parseFloat(tax) / parseFloat(value)) * 100;
      return Math.round(percentage * 100) / 100; // Round to 2 decimal places
    }
    
    return 0;
  }

  // Enhanced form validation helper
  validateHouseType(type: string): boolean {
    const value = this.unionFrom.get(type)?.value;
    const tax = this.unionFrom.get(type + '_kor')?.value;
    
    // If either value or tax is provided, both should be provided and valid
    if ((value && parseFloat(value) > 0) || (tax && parseFloat(tax) > 0)) {
      return !!(value && parseFloat(value) > 0 && tax && parseFloat(tax) > 0);
    }
    
    return true; // Valid if both are empty
  }

  // Currency display helper
  formatDisplayCurrency(value: any): string {
    if (value && !isNaN(value)) {
      return new Intl.NumberFormat('bn-BD', {
        style: 'currency',
        currency: 'BDT',
        minimumFractionDigits: 0
      }).format(value);
    }
    return '০ টাকা';
  }

  // SMS Configuration Helper Methods
  getActiveSMSCount(): number {
    let count = 0;
    if (this.unionFrom.get('is_create_assessment_msg_on')?.value) count++;
    if (this.unionFrom.get('is_tax_collection_msg_on')?.value) count++;
    return count;
  }

  getCharacterCount(fieldName: string): number {
    const value = this.unionFrom.get(fieldName)?.value;
    return value ? value.length : 0;
  }

  getSMSCount(fieldName: string): number {
    const length = this.getCharacterCount(fieldName);
    if (length === 0) return 0;
    return Math.ceil(length / 160); // Standard SMS length is 160 characters
  }

  updateCharacterCount(type: string): void {
    // This method is called on input but actual counting is done by getCharacterCount
    // Could be used for additional validation or formatting in the future
  }

  previewSMS(type: string): void {
    this.showPreview[type] = true;
  }

  closePreview(type: string): void {
    this.showPreview[type] = false;
  }

  getPreviewText(fieldName: string): string {
    let text = this.unionFrom.get(fieldName)?.value || '';
    
    // Replace common variables with sample data for preview
    const sampleData: {[key: string]: string} = {
      '{{tax_amount}}': '১,৫০০',
      '{{assessment.ward_number}}': '০৫',
      '{{assessment.ward_name}}': 'নং ওয়ার্ড',
      '{{assessment.holding_number}}': 'HC-১২৩৪',
      '{{assessment.name}}': 'মোঃ রহিম উদ্দিন',
      '{{assessment.gurdian_name}}': 'মোঃ করিম উদ্দিন',
      '{{assessment.mobile_number}}': '০১৭১১১১১১১১',
      '{{assessment.gram_or_rasta}}': 'মধ্যপাড়া',
      '{{assessment.post_office}}': 'ডুমুরিয়া',
      '{{assessment.qr_code}}': 'QR123456',
      '{{union.union_name}}': this.unionFrom.get('union_name')?.value || 'ডুমুরিয়া ইউনিয়ন',
      '{{union.upozilla_name}}': this.unionFrom.get('upozilla_name')?.value || 'ডুমুরিয়া',
      '{{union.zilla_name}}': this.unionFrom.get('zilla_name')?.value || 'খুলনা'
    };

    // Replace variables in the text
    for (const variable in sampleData) {
      if (sampleData.hasOwnProperty(variable)) {
        const regex = new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g');
        text = text.replace(regex, sampleData[variable as keyof typeof sampleData]);
      }
    }

    return text || 'এসএমএস টেমপ্লেট খালি আছে';
  }

  validateSMSTemplate(fieldName: string): boolean {
    const value = this.unionFrom.get(fieldName)?.value;
    if (!value) return true; // Empty is valid
    
    // Check for unmatched variables
    const variablePattern = /\{\{[^}]*\}\}/g;
    const variables = value.match(variablePattern) || [];
    
    const validVariables = [
      '{{tax_amount}}', '{{assessment.ward_number}}', '{{assessment.ward_name}}',
      '{{assessment.holding_number}}', '{{assessment.name}}', '{{assessment.gurdian_name}}',
      '{{assessment.mobile_number}}', '{{assessment.gram_or_rasta}}', '{{assessment.post_office}}',
      '{{assessment.qr_code}}', '{{union.union_name}}', '{{union.upozilla_name}}', '{{union.zilla_name}}'
    ];
    
    // Check if all used variables are valid
    return variables.every((variable: string) => validVariables.includes(variable));
  }

  private getUnion =  () => {
    this.isLoading = true;
    this.$subscriptions.push(this.unionService.getSingleUnion().subscribe({
      next: (res) => {
        this.initializeForm(res);
        this.isLoading = false;
      },
      error: (err) => {
        this.toastr.error(err.message);
        this.isLoading = false;
      }
    }));
  };

  private initializeForm =  (formData?: Union) => {
    this.unionFrom = new FormGroup({
      id: new FormControl(formData?.id),
      union_name: new FormControl({ 
        value: formData?.union_name, 
        disabled: true 
      }, [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      upozilla_name: new FormControl({ 
        value: formData?.upozilla_name, 
        disabled: true 
      }, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
      zilla_name: new FormControl({ 
        value: formData?.zilla_name, 
        disabled: true 
      }, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
      
      // House value fields with numeric validation
      ek_chala: new FormControl({ value: formData?.ek_chala, disabled: true }),
      dui_chala: new FormControl({ value: formData?.dui_chala, disabled: true }),
      char_chala: new FormControl({ value: formData?.char_chala, disabled: true }),
      adha_paka: new FormControl({ value: formData?.adha_paka, disabled: true }),
      paka: new FormControl({ value: formData?.paka, disabled: true }),
      ek_tola: new FormControl({ value: formData?.ek_tola, disabled: true }),
      dui_tola: new FormControl({ value: formData?.dui_tola, disabled: true }),
      tin_tola: new FormControl({ value: formData?.tin_tola, disabled: true }),
      char_tola: new FormControl({ value: formData?.char_tola, disabled: true }),
      bohutol: new FormControl({ value: formData?.bohutol, disabled: true }),
      
      // Tax amount fields with numeric validation
      ek_chala_kor: new FormControl({ value: formData?.ek_chala_kor, disabled: true }),
      dui_chala_kor: new FormControl({ value: formData?.dui_chala_kor, disabled: true }),
      char_chala_kor: new FormControl({ value: formData?.char_chala_kor, disabled: true }),
      adha_paka_kor: new FormControl({ value: formData?.adha_paka_kor, disabled: true }),
      paka_kor: new FormControl({ value: formData?.paka_kor, disabled: true }),
      ek_tola_kor: new FormControl({ value: formData?.ek_tola_kor, disabled: true }),
      dui_tola_kor: new FormControl({ value: formData?.dui_tola_kor, disabled: true }),
      tin_tola_kor: new FormControl({ value: formData?.tin_tola_kor, disabled: true }),
      char_tola_kor: new FormControl({ value: formData?.char_tola_kor, disabled: true }),
      bohutol_kor: new FormControl({ value: formData?.bohutol_kor, disabled: true }),
      
      // SMS configuration
      is_create_assessment_msg_on: new FormControl({value: formData?.is_create_assessment_msg_on,disabled:true}),
      is_tax_collection_msg_on:new FormControl({value: formData?.is_tax_collection_msg_on,disabled:true}),
      create_assessment_msg:new FormControl({
        value: formData?.create_assessment_msg,
        disabled:true
      }, [Validators.maxLength(500)]),
      tax_collection_msg:new FormControl({
        value: formData?.tax_collection_msg,
        disabled:true
      }, [Validators.maxLength(500)]),
      
      // Financial year with validation
      current_financial_year: new FormControl({
        value: formData?.current_financial_year || new Date().getFullYear(), 
        disabled:true
      }, [Validators.required]),
    })
  };

  editForm = (): void => {
    this.unionFrom.get('union_name')?.enable();
    this.unionFrom.get('upozilla_name')?.enable();
    this.unionFrom.get('zilla_name')?.enable();
    this.unionFrom.get('ek_chala')?.enable();
    this.unionFrom.get('dui_chala')?.enable();
    this.unionFrom.get('char_chala')?.enable();
    this.unionFrom.get('adha_paka')?.enable();
    this.unionFrom.get('paka')?.enable();
    this.unionFrom.get('ek_tola')?.enable();
    this.unionFrom.get('dui_tola')?.enable();
    this.unionFrom.get('tin_tola')?.enable();
    this.unionFrom.get('char_tola')?.enable();
    this.unionFrom.get('bohutol')?.enable();
    this.unionFrom.get('ek_chala_kor')?.enable();
    this.unionFrom.get('dui_chala_kor')?.enable();
    this.unionFrom.get('char_chala_kor')?.enable();
    this.unionFrom.get('adha_paka_kor')?.enable();
    this.unionFrom.get('paka_kor')?.enable();
    this.unionFrom.get('ek_tola_kor')?.enable();
    this.unionFrom.get('dui_tola_kor')?.enable();
    this.unionFrom.get('tin_tola_kor')?.enable();
    this.unionFrom.get('char_tola_kor')?.enable();
    this.unionFrom.get('bohutol_kor')?.enable();
    this.unionFrom.get('is_create_assessment_msg_on')?.enable();
    this.unionFrom.get('is_tax_collection_msg_on')?.enable();
    this.unionFrom.get('create_assessment_msg')?.enable();
    this.unionFrom.get('tax_collection_msg')?.enable();
    this.unionFrom.get('current_financial_year')?.enable();
    this.editUnion = true;
  };

  cancelForm = (): void => {
    this.unionFrom.get('union_name')?.disable();
    this.unionFrom.get('upozilla_name')?.disable();
    this.unionFrom.get('zilla_name')?.disable();
    this.unionFrom.get('ek_chala')?.disable();
    this.unionFrom.get('dui_chala')?.disable();
    this.unionFrom.get('char_chala')?.disable();
    this.unionFrom.get('adha_paka')?.disable();
    this.unionFrom.get('paka')?.disable();
    this.unionFrom.get('ek_tola')?.disable();
    this.unionFrom.get('dui_tola')?.disable();
    this.unionFrom.get('tin_tola')?.disable();
    this.unionFrom.get('char_tola')?.disable();
    this.unionFrom.get('bohutol')?.disable();
    this.unionFrom.get('ek_chala_kor')?.disable();
    this.unionFrom.get('dui_chala_kor')?.disable();
    this.unionFrom.get('char_chala_kor')?.disable();
    this.unionFrom.get('adha_paka_kor')?.disable();
    this.unionFrom.get('paka_kor')?.disable();
    this.unionFrom.get('ek_tola_kor')?.disable();
    this.unionFrom.get('dui_tola_kor')?.disable();
    this.unionFrom.get('tin_tola_kor')?.disable();
    this.unionFrom.get('char_tola_kor')?.disable();
    this.unionFrom.get('bohutol_kor')?.disable();
    this.unionFrom.get('is_create_assessment_msg_on')?.disable();
    this.unionFrom.get('is_tax_collection_msg_on')?.disable();
    this.unionFrom.get('create_assessment_msg')?.disable();
    this.unionFrom.get('tax_collection_msg')?.disable();
    this.unionFrom.get('current_financial_year')?.disable();
    this.editUnion = false;
  };

  saveForm = (): void => {
    if (this.unionFrom.get('id')?.value) {
      this.$subscriptions.push(this.unionService.updateUnion(this.unionFrom.value).subscribe({
        next: (res) => {
          this.toastr.success(res.message);
          this.ngOnInit();
        },
        error: (err) => {
          this.toastr.error(err.message);
        }
      }));
    } else {
      this.toastr.error('ইউনিয়নে সমস্যা হচ্ছে');
    }
  };

  // Initialize SMS Variables
  initializeVariables(): void {
    this.allVariables = [
      // Tax related variables
      { code: '{{tax_amount}}', label: 'করের পরিমাণ', category: 'tax' },
      
      // Assessment related variables
      { code: '{{assessment.ward_number}}', label: 'ওয়ার্ড নম্বর', category: 'assessment' },
      { code: '{{assessment.ward_name}}', label: 'ওয়ার্ডের নাম', category: 'assessment' },
      { code: '{{assessment.holding_number}}', label: 'হোল্ডিং নম্বর', category: 'assessment' },
      { code: '{{assessment.name}}', label: 'নাম', category: 'assessment' },
      { code: '{{assessment.gurdian_name}}', label: 'অভিভাবকের নাম', category: 'assessment' },
      { code: '{{assessment.mobile_number}}', label: 'মোবাইল নম্বর', category: 'assessment' },
      { code: '{{assessment.gram_or_rasta}}', label: 'গ্রাম বা রাস্তা', category: 'assessment' },
      { code: '{{assessment.post_office}}', label: 'পোস্ট অফিস', category: 'assessment' },
      { code: '{{assessment.qr_code}}', label: 'কিউআর কোড', category: 'assessment' },
      
      // Union related variables
      { code: '{{union.union_name}}', label: 'ইউনিয়নের নাম', category: 'union' },
      { code: '{{union.upozilla_name}}', label: 'উপজেলার নাম', category: 'union' },
      { code: '{{union.zilla_name}}', label: 'জেলার নাম', category: 'union' }
    ];
    
    this.filteredVariables = [...this.allVariables];
  }

  // Variables filtering and search
  filterVariables(): void {
    if (!this.variableSearchTerm.trim()) {
      this.filteredVariables = [...this.allVariables];
      return;
    }
    
    const searchTerm = this.variableSearchTerm.toLowerCase();
    this.filteredVariables = this.allVariables.filter((variable: any) => 
      variable.code.toLowerCase().includes(searchTerm) || 
      variable.label.toLowerCase().includes(searchTerm)
    );
  }

  getFilteredVariables(category: string): any[] {
    return this.filteredVariables.filter((variable: any) => variable.category === category);
  }

  shouldShowCategory(category: string): boolean {
    return this.getFilteredVariables(category).length > 0;
  }

  getVisibleVariablesCount(): number {
    return this.filteredVariables.length;
  }

  clearSearch(): void {
    this.variableSearchTerm = '';
    this.filterVariables();
  }

  // Copy to clipboard functionality
  async copyToClipboard(code: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      
      // Visual feedback
      this.recentlyCopied = code;
      this.lastCopiedVariable = label;
      this.showCopyToast = true;
      
      // Clear the visual feedback after a delay
      setTimeout(() => {
        this.recentlyCopied = '';
      }, 1000);
      
      setTimeout(() => {
        this.showCopyToast = false;
      }, 3000);
      
    } catch (err) {
      // Fallback for older browsers
      this.fallbackCopyToClipboard(code);
      this.lastCopiedVariable = label;
      this.showCopyToast = true;
      
      setTimeout(() => {
        this.showCopyToast = false;
      }, 3000);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }
    
    document.body.removeChild(textArea);
  }
}
